import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, money, money0, pct, qty } from '../lib/format';
import { PageHeader } from '../components/Layout';
import {
  ConfirmDialog,
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Meter,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface MenuRow {
  id: string;
  sku: string;
  name: string;
  categoryId: string;
  category: string;
  price: number;
  gstRate: number;
  station: string;
  isVegetarian: boolean;
  isActive: boolean;
  plateCost: number;
  foodCostPct: number;
  margin: number;
  marginPct: number;
  suggestedPrice: number;
  delta: number;
  flag: string;
  componentCount: number;
}

interface MenuDetail extends MenuRow {
  recipe: Array<{ ingredientId: string; name: string; unit: string; quantity: number; unitCost: number; lineCost: number }>;
  priceHistory: Array<{ id: string; oldPrice: number; newPrice: number; reason: string; changedAt: string }>;
}

interface Category {
  id: string;
  name: string;
}

interface Ingredient {
  id: string;
  name: string;
  unit: string;
  avgCost: number;
}

export function MenuPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [bulk, setBulk] = useState(false);
  const [creating, setCreating] = useState(false);

  const menu = useQuery({ queryKey: ['menu'], queryFn: () => api.get<MenuRow[]>('/api/menu') });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.get<Category[]>('/api/categories') });
  const settings = useQuery({
    queryKey: ['property'],
    queryFn: () => api.get<{ settings: { targetFoodCostPct: number } }>('/api/property'),
  });

  const target = settings.data?.settings.targetFoodCostPct ?? 32;

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (menu.data ?? []).filter(
      (item) =>
        (category === 'All' || item.category === category) &&
        (term === '' || item.name.toLowerCase().includes(term) || item.sku.toLowerCase().includes(term)),
    );
  }, [menu.data, category, search]);

  if (menu.isLoading) return <Loading label="Costing the menu…" />;
  if (menu.error) return <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />;

  const all = menu.data ?? [];
  const avgFoodCost = rows.length ? rows.reduce((sum, r) => sum + r.foodCostPct, 0) / rows.length : 0;
  const lowMargin = all.filter((r) => r.flag === 'LOW_MARGIN');
  const noRecipe = all.filter((r) => r.flag === 'NO_RECIPE');

  return (
    <div className="stack">
      <PageHeader
        title="Menu & Pricing"
        subtitle="Recipe costing, margin control and auditable price revisions"
        actions={
          can('menu:price') ? (
            <>
              <button className="btn" onClick={() => setBulk(true)}>
                Bulk reprice
              </button>
              {can('menu:manage') ? (
                <button className="btn primary" onClick={() => setCreating(true)}>
                  + New item
                </button>
              ) : null}
            </>
          ) : null
        }
      />

      <div className="kpis">
        <Kpi label="Menu items" value={all.length} foot={`${categories.data?.length ?? 0} categories`} />
        <Kpi
          label="Average food cost"
          value={pct(avgFoodCost)}
          foot={`Target ${pct(target)}`}
          tone={avgFoodCost > target ? 'amber' : ''}
        />
        <Kpi label="Below margin" value={lowMargin.length} foot="More than 8 points over target" tone={lowMargin.length ? 'red' : ''} />
        <Kpi label="Missing a recipe" value={noRecipe.length} foot="Cannot be costed yet" tone={noRecipe.length ? 'amber' : ''} />
      </div>

      <div className="card">
        <div className="card-head">
          <div className="tabs">
            {['All', ...(categories.data ?? []).map((c) => c.name)].map((name) => (
              <button key={name} className={`tab ${name === category ? 'active' : ''}`} onClick={() => setCategory(name)}>
                {name}
              </button>
            ))}
          </div>
          <div className="search" style={{ width: 210 }}>
            <input className="input" placeholder="Search item" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {rows.length === 0 ? (
          <Empty message="No items match" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th className="num">Price</th>
                  <th className="num">Plate cost</th>
                  <th className="num">Food cost</th>
                  <th className="num">Margin</th>
                  <th className="num">Suggested</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.id} className="clickable" onClick={() => setOpenId(item.id)}>
                    <td>
                      <div className="row gap-6">
                        <span className={`veg-dot ${item.isVegetarian ? '' : 'nonveg'}`} />
                        <span className="b">{item.name}</span>
                      </div>
                      <div className="xs muted mono">
                        {item.sku} · {item.station.toLowerCase()} · GST {item.gstRate}%
                      </div>
                    </td>
                    <td className="dim">{item.category}</td>
                    <td className="num b">{money0(item.price)}</td>
                    <td className="num">{item.plateCost > 0 ? money(item.plateCost) : <span className="muted">—</span>}</td>
                    <td className="num">
                      <span
                        className={`chip ${
                          item.foodCostPct > target + 8 ? 'danger' : item.foodCostPct > target ? 'warn' : 'ok'
                        }`}
                      >
                        {pct(item.foodCostPct)}
                      </span>
                    </td>
                    <td className="num">
                      {money0(item.margin)}
                      <div className="xs muted">{pct(item.marginPct)}</div>
                    </td>
                    <td className={`num ${Math.abs(item.delta) >= 5 ? 'b' : 'dim'}`}>
                      {money0(item.suggestedPrice)}
                      {Math.abs(item.delta) >= 5 ? (
                        <div className={`xs ${item.delta > 0 ? 'warn' : 'dim'}`}>
                          {item.delta > 0 ? '▲' : '▼'} {money0(Math.abs(item.delta))}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <StatusChip status={item.flag} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId ? <ItemDrawer id={openId} target={target} onClose={() => setOpenId(null)} /> : null}
      {bulk ? (
        <BulkRepriceModal
          target={target}
          categories={categories.data ?? []}
          onClose={() => setBulk(false)}
          onDone={() => {
            setBulk(false);
            void queryClient.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      ) : null}
      {creating ? (
        <NewItemModal
          categories={categories.data ?? []}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      ) : null}
    </div>
  );
}

function ItemDrawer({ id, target, onClose }: { id: string; target: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [price, setPrice] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [recipe, setRecipe] = useState<Array<{ ingredientId: string; quantity: number }> | null>(null);

  const item = useQuery({ queryKey: ['menu', id], queryFn: () => api.get<MenuDetail>(`/api/menu/${id}`) });
  const ingredients = useQuery({ queryKey: ['ingredients'], queryFn: () => api.get<Ingredient[]>('/api/ingredients') });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['menu'] });
    void queryClient.invalidateQueries({ queryKey: ['menu', id] });
  };

  const savePrice = useMutation({
    mutationFn: (input: { price: number; priceChangeReason: string }) => api.patch(`/api/menu/${id}`, input),
    onSuccess: () => {
      push('Price revised and logged', 'ok');
      setPrice(null);
      setReason('');
      invalidate();
    },
    onError: fail,
  });

  const saveRecipe = useMutation({
    mutationFn: (components: Array<{ ingredientId: string; quantity: number }>) =>
      api.put(`/api/menu/${id}/recipe`, { components }),
    onSuccess: () => {
      push('Recipe saved — costs recalculated', 'ok');
      setRecipe(null);
      invalidate();
    },
    onError: fail,
  });

  const data = item.data;
  const lines = recipe ?? data?.recipe.map((r) => ({ ingredientId: r.ingredientId, quantity: r.quantity })) ?? [];

  return (
    <Drawer title={data?.name ?? 'Menu item'} size="wide" onClose={onClose}>
      {item.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          <div className="kpis">
            <Kpi label="Selling price" value={money0(data.price)} foot={`GST ${data.gstRate}% on top`} />
            <Kpi label="Plate cost" value={money(data.plateCost)} foot={`${data.recipe.length} ingredient(s)`} />
            <Kpi
              label="Food cost"
              value={pct(data.foodCostPct)}
              foot={`Margin ${money0(data.margin)}`}
              tone={data.flag === 'LOW_MARGIN' ? 'red' : ''}
            />
            <Kpi label="Suggested" value={money0(data.suggestedPrice)} foot={`At ${pct(target)} food cost`} tone="amber" />
          </div>

          {can('menu:price') ? (
            <div className="card">
              <div className="card-head">
                <h2>Revise price</h2>
              </div>
              <div className="card-body col gap-16">
                <div className="form-grid">
                  <TextInput
                    label="New price (₹)"
                    type="number"
                    value={price ?? data.price}
                    onChange={(e) => setPrice(Number(e.target.value) || 0)}
                  />
                  <TextInput
                    label="Reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. input cost increase"
                  />
                </div>
                <div className="row gap-6">
                  <button
                    className="btn primary"
                    disabled={savePrice.isPending}
                    onClick={() =>
                      savePrice.mutate({ price: price ?? data.price, priceChangeReason: reason || 'Manual revision' })
                    }
                  >
                    Apply price
                  </button>
                  <button
                    className="btn"
                    disabled={savePrice.isPending || data.plateCost === 0}
                    onClick={() =>
                      savePrice.mutate({
                        price: data.suggestedPrice,
                        priceChangeReason: `Aligned to ${target}% food cost`,
                      })
                    }
                  >
                    Use suggested {money0(data.suggestedPrice)}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-head">
              <h2>Recipe</h2>
              {can('menu:manage') ? (
                <button
                  className="btn sm"
                  onClick={() => setRecipe([...lines, { ingredientId: '', quantity: 0 }])}
                >
                  + Ingredient
                </button>
              ) : null}
            </div>
            <div className="card-body">
              {lines.length === 0 ? (
                <Empty message="No recipe yet — this item cannot be costed" />
              ) : (
                <div className="col gap-6">
                  {lines.map((line, index) => (
                    <div className="row gap-6" key={index}>
                      <select
                        className="select"
                        style={{ flex: 2 }}
                        value={line.ingredientId}
                        disabled={!can('menu:manage')}
                        onChange={(e) => {
                          const next = [...lines];
                          next[index] = { ...line, ingredientId: e.target.value };
                          setRecipe(next);
                        }}
                      >
                        <option value="">— select ingredient —</option>
                        {(ingredients.data ?? []).map((ingredient) => (
                          <option key={ingredient.id} value={ingredient.id}>
                            {ingredient.name} ({ingredient.unit.toLowerCase()}) · {money(ingredient.avgCost)}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input"
                        type="number"
                        step="0.001"
                        style={{ width: 110 }}
                        value={line.quantity}
                        disabled={!can('menu:manage')}
                        onChange={(e) => {
                          const next = [...lines];
                          next[index] = { ...line, quantity: Number(e.target.value) || 0 };
                          setRecipe(next);
                        }}
                      />
                      {can('menu:manage') ? (
                        <button
                          className="btn ghost icon sm"
                          onClick={() => setRecipe(lines.filter((_, i) => i !== index))}
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              {recipe && can('menu:manage') ? (
                <div className="row gap-6" style={{ marginTop: 12 }}>
                  <button
                    className="btn primary sm"
                    disabled={saveRecipe.isPending}
                    onClick={() => saveRecipe.mutate(recipe.filter((l) => l.ingredientId && l.quantity > 0))}
                  >
                    Save recipe
                  </button>
                  <button className="btn sm" onClick={() => setRecipe(null)}>
                    Cancel
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {data.recipe.length > 0 ? (
            <div className="card">
              <div className="card-head">
                <h2>Cost breakdown</h2>
              </div>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Ingredient</th>
                      <th className="num">Quantity</th>
                      <th className="num">Unit cost</th>
                      <th className="num">Line cost</th>
                      <th className="num" style={{ width: 130 }}>
                        Share
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recipe.map((line) => (
                      <tr key={line.ingredientId}>
                        <td>{line.name}</td>
                        <td className="num">{qty(line.quantity, line.unit)}</td>
                        <td className="num dim">{money(line.unitCost)}</td>
                        <td className="num b">{money(line.lineCost)}</td>
                        <td>
                          <Meter value={data.plateCost ? (line.lineCost / data.plateCost) * 100 : 0} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Total plate cost</td>
                      <td className="num">{money(data.plateCost)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : null}

          <div className="card">
            <div className="card-head">
              <h2>Price history</h2>
            </div>
            {data.priceHistory.length === 0 ? (
              <Empty message="No revisions yet" />
            ) : (
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th className="num">From</th>
                      <th className="num">To</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.priceHistory.map((entry) => (
                      <tr key={entry.id}>
                        <td>{date(entry.changedAt)}</td>
                        <td className="num dim">{money0(entry.oldPrice)}</td>
                        <td className="num b">{money0(entry.newPrice)}</td>
                        <td className="small">{entry.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}

function BulkRepriceModal({
  target,
  categories,
  onClose,
  onDone,
}: {
  target: number;
  categories: Category[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const [mode, setMode] = useState<'TARGET_FOOD_COST' | 'UPLIFT'>('TARGET_FOOD_COST');
  const [categoryId, setCategoryId] = useState('');
  const [targetPct, setTargetPct] = useState(target);
  const [uplift, setUplift] = useState(5);
  const [confirming, setConfirming] = useState(false);

  const apply = useMutation({
    mutationFn: () =>
      api.post<{ changed: number }>('/api/pricing/bulk', {
        mode,
        categoryId: categoryId || undefined,
        targetFoodCostPct: mode === 'TARGET_FOOD_COST' ? targetPct : undefined,
        upliftPct: mode === 'UPLIFT' ? uplift : undefined,
        reason: mode === 'UPLIFT' ? `Bulk uplift ${uplift}%` : `Repriced to ${targetPct}% food cost`,
      }),
    onSuccess: (result) => {
      push(`${result.changed} item(s) repriced`, 'ok');
      onDone();
    },
    onError: (error) => {
      setConfirming(false);
      fail(error);
    },
  });

  if (confirming) {
    return (
      <ConfirmDialog
        message={`Reprice ${categoryId ? categories.find((c) => c.id === categoryId)?.name : 'the whole menu'} using ${
          mode === 'UPLIFT' ? `a ${uplift}% uplift` : `${targetPct}% food cost`
        }? Every change is written to price history.`}
        confirmLabel="Reprice"
        busy={apply.isPending}
        onConfirm={() => apply.mutate()}
        onCancel={() => setConfirming(false)}
      />
    );
  }

  return (
    <Modal
      title="Bulk reprice"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => setConfirming(true)}>
            Review
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <p className="dim small">
          Prices round up to the nearest ₹5. Items without a recipe are skipped when repricing to a food-cost target.
        </p>
        <div className="form-grid">
          <Select label="Category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
          <Select label="Method" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
            <option value="TARGET_FOOD_COST">Align to target food cost</option>
            <option value="UPLIFT">Flat percentage uplift</option>
          </Select>
          {mode === 'TARGET_FOOD_COST' ? (
            <TextInput
              label="Target food cost %"
              type="number"
              value={targetPct}
              onChange={(e) => setTargetPct(Number(e.target.value) || target)}
            />
          ) : (
            <TextInput
              label="Uplift %"
              type="number"
              value={uplift}
              onChange={(e) => setUplift(Number(e.target.value) || 0)}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

function NewItemModal({
  categories,
  onClose,
  onDone,
}: {
  categories: Category[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const [form, setForm] = useState({
    name: '',
    categoryId: categories[0]?.id ?? '',
    price: 0,
    gstRate: 5,
    station: 'KITCHEN',
    isVegetarian: true,
    hsn: '996331',
    isActive: true,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/menu', form),
    onSuccess: () => {
      push('Item created — add its recipe to enable costing', 'ok');
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title="New menu item"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || !form.categoryId || create.isPending}
            onClick={() => create.mutate()}
          >
            Create item
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          className="span2"
          label="Name *"
          autoFocus
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Select label="Category" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
        <TextInput
          label="Price (₹, pre-GST)"
          type="number"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: Number(e.target.value) || 0 })}
        />
        <Select
          label="GST rate"
          value={String(form.gstRate)}
          onChange={(e) => setForm({ ...form, gstRate: Number(e.target.value) })}
        >
          <option value="5">5% — restaurant service</option>
          <option value="12">12%</option>
          <option value="18">18%</option>
        </Select>
        <Select label="Kitchen station" value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })}>
          {['KITCHEN', 'GRILL', 'FRYER', 'OVEN', 'BEVERAGE', 'BAKERY'].map((station) => (
            <option key={station} value={station}>
              {station[0] + station.slice(1).toLowerCase()}
            </option>
          ))}
        </Select>
        <Select
          label="Food type"
          value={form.isVegetarian ? 'veg' : 'nonveg'}
          onChange={(e) => setForm({ ...form, isVegetarian: e.target.value === 'veg' })}
        >
          <option value="veg">Vegetarian</option>
          <option value="nonveg">Non-vegetarian</option>
        </Select>
      </div>
    </Modal>
  );
}
